import { useState, useEffect, useRef } from 'react';
import {
  Plus, X, Check, AlertCircle, Trash2, Edit2,
  Video, Shield, Brain, RefreshCw, Wifi, Phone,
  ChevronRight, ChevronLeft, Server, Zap, Eye,
  Settings2, CheckCircle2, Circle, ArrowRight, Loader2
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';
// CSS loaded globally via src/styles/v2/index.css → pages/integrations-manager.css

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = {
  VMS:            { label: 'Video Management',  Icon: Video,   color: '#60a5fa', bg: 'rgba(96,165,250,0.12)'  },
  ACCESS_CONTROL: { label: 'Access Control',    Icon: Shield,  color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  AI_ANALYTICS:   { label: 'AI & Analytics',    Icon: Brain,   color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  INTERCOM:       { label: 'Intercoms',          Icon: Phone,   color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
};

const STEPS = ['Choose System', 'Configure', 'Test Connection', 'Review'];

// ─── Inline SVG Logos ─────────────────────────────────────────────────────────

const LOGO_URLS = {
  WAVE_VMS:        'https://logo.clearbit.com/hanwhavision.com',
  MIRASYS_VMS:     'https://logo.clearbit.com/mirasys.com',
  MILESTONE_VMS:   'https://logo.clearbit.com/milestonesys.com',
  FEENICS_ACCESS:  'https://logo.clearbit.com/acre-security.com',
  GENETEC_ACS:     'https://logo.clearbit.com/genetec.com',
  GENETEC_VMS:     'https://logo.clearbit.com/genetec.com',
  AXIS_VAPIX:      'https://logo.clearbit.com/axis.com',
  OOSTO_FR:        'https://logo.clearbit.com/oosto.com',
  INTERCOM_2N:     'https://logo.clearbit.com/2n.com',
};

const LogoImg = ({ type, size = 26 }) => {
  const [err, setErr] = useState(false);
  const url = LOGO_URLS[type];
  if (!url || err) return null;
  return (
    <img
      src={url}
      alt={type}
      width={size}
      height={size}
      style={{ borderRadius: 6, objectFit: 'contain', background: '#fff', padding: 2 }}
      onError={() => setErr(true)}
    />
  );
};

const LOGOS = {
  WAVE_VMS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#0B5BA8"/>
      <path d="M8 24L13 14l5 8 4-6 5 9 5-11" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="32" cy="13" r="2.5" fill="#00C6FF"/>
    </svg>
  ),
  MIRASYS_VMS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#1A1A2E"/>
      <circle cx="20" cy="20" r="9" stroke="#E94560" strokeWidth="2.5"/>
      <circle cx="20" cy="20" r="4" fill="#E94560"/>
      <path d="M20 11V8M20 32v-3M29 20h3M8 20h3" stroke="#E94560" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  MILESTONE_VMS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#D4001E"/>
      <path d="M8 28V14l6 9 6-9v14M26 14h6M26 21h5M26 28h6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  FEENICS_ACCESS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#0F4C81"/>
      <rect x="13" y="10" width="14" height="10" rx="2" stroke="#fff" strokeWidth="2"/>
      <path d="M17 20v2a3 3 0 006 0v-2" stroke="#fff" strokeWidth="2"/>
      <circle cx="20" cy="27" r="2" fill="#4FC3F7"/>
      <path d="M20 29v3" stroke="#4FC3F7" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  OOSTO_FR: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#6C2BD9"/>
      <circle cx="20" cy="17" r="5" stroke="#fff" strokeWidth="2"/>
      <path d="M10 32c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M26 10l2-2M28 14h2.5M26 18l2 2" stroke="#C4B5FD" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  INTERCOM_2N: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#1B5E20"/>
      <rect x="12" y="9" width="16" height="22" rx="3" stroke="#fff" strokeWidth="2"/>
      <circle cx="20" cy="17" r="3.5" stroke="#69F0AE" strokeWidth="1.8"/>
      <rect x="16" y="23" width="8" height="2" rx="1" fill="#69F0AE"/>
      <rect x="17" y="26" width="6" height="1.5" rx="0.75" fill="rgba(255,255,255,0.4)"/>
    </svg>
  ),
  GENETEC_ACS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#1a3a5c"/>
      <path d="M12 16h16v12H12z" stroke="#60a5fa" strokeWidth="1.8" rx="2"/>
      <path d="M20 22v3" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="20" cy="21" r="2" stroke="#fff" strokeWidth="1.5"/>
      <path d="M15 16v-3a5 5 0 0 1 10 0v3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  AXIS_VAPIX: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#FFC300"/>
      <rect x="12" y="14" width="16" height="12" rx="2" stroke="#1a1a1a" strokeWidth="2"/>
      <circle cx="20" cy="20" r="3" stroke="#1a1a1a" strokeWidth="1.5"/>
      <path d="M20 17v-4M16 14l-2-2M24 14l2-2" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  GENETEC_VMS: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="26" height="26">
      <rect width="40" height="40" rx="8" fill="#1a3a5c"/>
      <rect x="9" y="10" width="22" height="15" rx="2" stroke="#fff" strokeWidth="2"/>
      <path d="M17 18l3 2.5L23 17" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M15 28h10M20 25v3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  return (
    <div className="im-steps">
      {STEPS.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'idle';
        return (
          <div key={i} className={`im-step im-step--${state}`}>
            <div className="im-step__dot">
              {state === 'done' ? <Check size={12} /> : <span>{i + 1}</span>}
            </div>
            <span className="im-step__label">{label}</span>
            {i < STEPS.length - 1 && <div className="im-step__line" />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Choose System ────────────────────────────────────────────────────

function StepChooseSystem({ groupedTypes, onSelect }) {
  const [activeCategory, setActiveCategory] = useState(Object.keys(groupedTypes)[0] || 'VMS');

  return (
    <div className="im-choose">
      {/* Category Tabs */}
      <div className="im-category-tabs">
        {Object.entries(CATEGORIES).map(([key, { label, Icon, color }]) => {
          const types = groupedTypes[key] || [];
          if (!types.length) return null;
          return (
            <button
              key={key}
              className={`im-category-tab ${activeCategory === key ? 'im-category-tab--active' : ''}`}
              style={{ '--tab-color': color }}
              onClick={() => setActiveCategory(key)}
            >
              <Icon size={15} />
              <span>{label}</span>
              <span className="im-category-tab__count">{types.length}</span>
            </button>
          );
        })}
      </div>

      {/* System Cards */}
      <div className="im-type-grid">
        {(groupedTypes[activeCategory] || []).map(type => {
          const { color, bg, Icon } = CATEGORIES[activeCategory] || {};
          return (
            <button
              key={type.type}
              className="im-type-card"
              style={{ '--card-color': color, '--card-bg': bg }}
              onClick={() => onSelect(type)}
            >
              <div className="im-type-card__icon">
                {LOGO_URLS[type.type]
                  ? <LogoImg type={type.type} size={26} />
                  : Icon ? <Icon size={22} /> : <Server size={22} />
                }
              </div>
              <div className="im-type-card__body">
                <div className="im-type-card__name">{type.name}</div>
                <div className="im-type-card__desc">{type.description}</div>
              </div>
              <ArrowRight size={16} className="im-type-card__arrow" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 2: Configure ────────────────────────────────────────────────────────

function StepConfigure({ selectedType, configForm, setConfigForm, connectionMethod, setConnectionMethod, selectedAgentId, setSelectedAgentId, agents, vmsGroups }) {
  const [showAllCameras, setShowAllCameras] = useState(false);
  const { color } = CATEGORIES[selectedType.category] || {};

  // Intercom custom form
  const renderIntercomForm = () => {
    const selectedVmsId = configForm.vmsIntegrationId || '';
    const selectedGroup = vmsGroups.find(g => g.integrationId === selectedVmsId);
    const cameraList = showAllCameras
      ? (selectedGroup?.allCameras || [])
      : (selectedGroup?.cameras || []);

    return (
      <>
        <div className="im-field">
          <label className="im-label">Intercom Name <span className="im-required">*</span></label>
          <input
            type="text"
            className="im-input"
            placeholder="e.g. Front Door"
            value={configForm.name || ''}
            onChange={e => setConfigForm({ ...configForm, name: e.target.value })}
          />
        </div>

        <div className="im-field">
          <label className="im-label">VMS Integration</label>
          {vmsGroups.length === 0 ? (
            <div className="im-warning">
              <AlertCircle size={14} /> No VMS integrations found. Add a VMS first.
            </div>
          ) : (
            <select
              className="im-select"
              value={selectedVmsId}
              onChange={e => setConfigForm({ ...configForm, vmsIntegrationId: e.target.value, cameraId: '', cameraName: '' })}
            >
              <option value="">— Select VMS —</option>
              {vmsGroups.map(g => (
                <option key={g.integrationId} value={g.integrationId}>
                  {g.integrationName} ({g.vmsType})
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedVmsId && (
          <div className="im-field">
            <div className="im-label-row">
              <label className="im-label">Camera / Device</label>
              <button type="button" className="im-link" onClick={() => setShowAllCameras(!showAllCameras)}>
                {showAllCameras ? 'Show intercom models only' : 'Show all cameras'}
              </button>
            </div>
            {cameraList.length === 0 ? (
              <div className="im-hint">
                {showAllCameras ? 'No cameras found.' : 'No intercom models detected. Try "Show all cameras".'}
              </div>
            ) : (
              <select
                className="im-select"
                value={configForm.cameraId || ''}
                onChange={e => {
                  const cam = cameraList.find(c => c.id === e.target.value);
                  setConfigForm({ ...configForm, cameraId: e.target.value, cameraName: cam?.name || '' });
                }}
              >
                <option value="">— Select camera —</option>
                {cameraList.map(cam => (
                  <option key={cam.id} value={cam.id}>
                    {cam.name}{cam.model ? ` (${cam.model})` : ''}
                  </option>
                ))}
              </select>
            )}
            {!showAllCameras && selectedGroup && (
              <p className="im-hint">
                {selectedGroup.cameras.length} intercom device{selectedGroup.cameras.length !== 1 ? 's' : ''} detected
                {selectedGroup.allCameras.length > selectedGroup.cameras.length ? ` of ${selectedGroup.allCameras.length} total` : ''}
              </p>
            )}
          </div>
        )}

        <div className="im-field">
          <label className="im-label">n8n Webhook URL (Door Unlock) <span className="im-required">*</span></label>
          <input
            type="text"
            className="im-input"
            placeholder="https://n8n.example.com/webhook/..."
            value={configForm.n8nWebhookUrl || ''}
            onChange={e => setConfigForm({ ...configForm, n8nWebhookUrl: e.target.value })}
          />
          <p className="im-hint">Called when a user clicks "Unlock" on an active intercom call.</p>
        </div>
      </>
    );
  };

  // Generic schema-driven form
  const renderSchemaForm = () => {
    // Support both { properties: {} } (JSON Schema) and { fields: [] } (legacy) formats
    let props = selectedType.configSchema?.properties || {};
    if (Object.keys(props).length === 0 && Array.isArray(selectedType.configSchema?.fields)) {
      props = {};
      for (const f of selectedType.configSchema.fields) {
        props[f.key] = {
          title: f.label || f.key,
          type: f.type === 'password' ? 'string' : (f.type || 'string'),
          format: f.type === 'password' ? 'password' : undefined,
          placeholder: f.placeholder || '',
          description: f.helpText || '',
        };
      }
    }
    const requiredList = selectedType.configSchema?.required
      || (selectedType.configSchema?.fields || []).filter(f => f.required).map(f => f.key)
      || [];
    return Object.entries(props).map(([key, prop]) => {
      const isRequired = requiredList.includes(key);
      const val = configForm[key] !== undefined ? configForm[key] : '';
      const inputType = prop.format === 'password' ? 'password' : prop.type === 'number' ? 'number' : 'text';

      return (
        <div key={key} className="im-field">
          <label className="im-label">
            {prop.title || key}
            {isRequired && <span className="im-required"> *</span>}
          </label>
          {prop.description && <p className="im-hint im-hint--top">{prop.description}</p>}
          {prop.enum ? (
            <select
              className="im-select"
              value={val}
              onChange={e => setConfigForm({ ...configForm, [key]: e.target.value })}
            >
              {prop.enum.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : prop.type === 'boolean' ? (
            <label className="im-toggle">
              <input
                type="checkbox"
                checked={!!configForm[key]}
                onChange={e => setConfigForm({ ...configForm, [key]: e.target.checked })}
              />
              <span className="im-toggle__slider" />
              <span className="im-toggle__label">{prop.title || key}</span>
            </label>
          ) : (
            <input
              type={inputType}
              className="im-input"
              placeholder={prop.placeholder || ''}
              value={val}
              onChange={e => setConfigForm({
                ...configForm,
                [key]: prop.type === 'number' ? Number(e.target.value) : e.target.value
              })}
            />
          )}
        </div>
      );
    });
  };

  return (
    <div className="im-configure">
      {/* System Badge */}
      <div className="im-system-badge" style={{ '--badge-color': color }}>
        <div className="im-system-badge__dot" />
        <span>{selectedType.name}</span>
        <span className="im-system-badge__category">{CATEGORIES[selectedType.category]?.label}</span>
      </div>

      <div className="im-fields">
        {selectedType.category === 'INTERCOM' ? renderIntercomForm() : renderSchemaForm()}
      </div>

      {/* Connection Method */}
      <div className="im-connection-method">
        <div className="im-section-label">
          <Wifi size={13} />
          Connection Method
        </div>
        <div className="im-method-pills">
          {['DIRECT', 'AGENT_TUNNEL'].map(method => (
            <button
              key={method}
              type="button"
              className={`im-method-pill ${connectionMethod === method ? 'im-method-pill--active' : ''}`}
              onClick={() => setConnectionMethod(method)}
            >
              {method === 'DIRECT' ? (
                <><Zap size={13} /> Direct (VPN / Tailscale)</>
              ) : (
                <><Server size={13} /> Agent Tunnel</>
              )}
            </button>
          ))}
        </div>

        {connectionMethod === 'AGENT_TUNNEL' && (
          <div className="im-field im-field--indent">
            <label className="im-label">Select Agent</label>
            <select
              className="im-select"
              value={selectedAgentId}
              onChange={e => setSelectedAgentId(e.target.value)}
            >
              <option value="">— Select an agent —</option>
              {agents.filter(a => a.status === 'ONLINE' || a.status === 'DEGRADED').map(a => (
                <option key={a.id} value={a.id}>
                  {a.name || a.hostname || a.id} {a.status === 'ONLINE' ? '✅' : '⚠️'}
                </option>
              ))}
              {agents.filter(a => a.status !== 'ONLINE' && a.status !== 'DEGRADED').map(a => (
                <option key={a.id} value={a.id} disabled>
                  {a.name || a.hostname || a.id} (offline)
                </option>
              ))}
            </select>
            <p className="im-hint">Routes API calls through the local agent. No VPN required.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3: Test Connection ──────────────────────────────────────────────────

function StepTestConnection({ selectedType, configForm, connectionMethod, selectedAgentId, testResult, setTestResult }) {
  const [testing, setTesting] = useState(false);
  const [autoRan, setAutoRan] = useState(false);
  const { color } = CATEGORIES[selectedType.category] || {};

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetchWithTenant('/api/integrations/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType.type,
          config: configForm,
          connectionMethod,
          agentId: selectedAgentId || null
        })
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  // Auto-run on mount
  useEffect(() => {
    if (!autoRan) {
      setAutoRan(true);
      runTest();
    }
  }, []);

  return (
    <div className="im-test">
      <div className="im-test__visual">
        {testing ? (
          <div className="im-test__spinner" style={{ '--spin-color': color }}>
            <div className="im-test__spinner-ring" />
            <Loader2 size={28} className="im-spin" />
          </div>
        ) : testResult ? (
          <div className={`im-test__result-icon ${testResult.success ? 'im-test__result-icon--success' : 'im-test__result-icon--fail'}`}>
            {testResult.success ? <CheckCircle2 size={40} /> : <AlertCircle size={40} />}
          </div>
        ) : (
          <div className="im-test__idle">
            <Circle size={40} />
          </div>
        )}
      </div>

      <div className="im-test__status">
        {testing && (
          <>
            <p className="im-test__heading">Connecting to {selectedType.name}…</p>
            <p className="im-test__sub">Testing your credentials and network connectivity</p>
          </>
        )}
        {!testing && testResult && (
          <>
            <p className={`im-test__heading ${testResult.success ? 'im-test__heading--success' : 'im-test__heading--fail'}`}>
              {testResult.success ? 'Connection Successful' : 'Connection Failed'}
            </p>
            <p className="im-test__sub">
              {testResult.success
                ? (testResult.message || `Successfully reached ${selectedType.name}`)
                : (testResult.error || 'Could not reach the system with the provided credentials')}
            </p>
          </>
        )}
        {!testing && !testResult && (
          <p className="im-test__sub">Ready to test connection</p>
        )}
      </div>

      {/* Details chips */}
      <div className="im-test__details">
        <span className="im-chip"><Server size={11} /> {selectedType.name}</span>
        <span className="im-chip">
          <Wifi size={11} />
          {connectionMethod === 'AGENT_TUNNEL' ? 'Agent Tunnel' : 'Direct'}
        </span>
        {configForm.host && (
          <span className="im-chip">{configForm.host}{configForm.port ? `:${configForm.port}` : ''}</span>
        )}
      </div>

      {!testing && (
        <button className="im-btn im-btn--ghost" onClick={runTest}>
          <RefreshCw size={14} /> Run Test Again
        </button>
      )}
    </div>
  );
}

// ─── Step 4: Review ───────────────────────────────────────────────────────────

function StepReview({ selectedType, configForm, connectionMethod, selectedAgentId, testResult, agents }) {
  const { color, bg, Icon } = CATEGORIES[selectedType.category] || {};
  const agent = agents.find(a => a.id === selectedAgentId);

  const sensitiveKeys = ['password', 'apiKey', 'token', 'secret'];
  const isSensitive = key => sensitiveKeys.some(s => key.toLowerCase().includes(s));

  const displayConfig = Object.entries(configForm).filter(([k, v]) => v !== '' && v !== null && v !== undefined);

  return (
    <div className="im-review">
      {/* System summary card */}
      <div className="im-review__card" style={{ '--card-color': color, '--card-bg': bg }}>
        <div className="im-review__card-icon">
          {Icon && <Icon size={20} />}
        </div>
        <div className="im-review__card-body">
          <div className="im-review__card-name">
            {configForm.name || selectedType.name}
          </div>
          <div className="im-review__card-meta">
            {selectedType.name} · {CATEGORIES[selectedType.category]?.label}
          </div>
        </div>
        {testResult?.success && (
          <div className="im-review__test-badge">
            <Check size={12} /> Verified
          </div>
        )}
      </div>

      {/* Config summary */}
      <div className="im-review__section">
        <p className="im-review__section-label">Configuration</p>
        <div className="im-review__rows">
          {displayConfig.map(([key, val]) => (
            <div key={key} className="im-review__row">
              <span className="im-review__row-key">{key}</span>
              <span className="im-review__row-val">
                {isSensitive(key) ? '••••••••' : String(val)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Connection */}
      <div className="im-review__section">
        <p className="im-review__section-label">Connection</p>
        <div className="im-review__rows">
          <div className="im-review__row">
            <span className="im-review__row-key">Method</span>
            <span className="im-review__row-val">
              {connectionMethod === 'AGENT_TUNNEL' ? 'Agent Tunnel' : 'Direct (VPN / Tailscale)'}
            </span>
          </div>
          {agent && (
            <div className="im-review__row">
              <span className="im-review__row-key">Agent</span>
              <span className="im-review__row-val">{agent.name || agent.hostname}</span>
            </div>
          )}
        </div>
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`im-review__test-result ${testResult.success ? 'im-review__test-result--ok' : 'im-review__test-result--fail'}`}>
          {testResult.success
            ? <><CheckCircle2 size={14} /> Connection verified</>
            : <><AlertCircle size={14} /> Connection test failed — you can still save and fix credentials later</>
          }
        </div>
      )}
    </div>
  );
}

// ─── Existing Integration Card ────────────────────────────────────────────────

function IntegrationCard({ integration, onEdit, onDelete, onTest }) {
  const { color, Icon } = CATEGORIES[integration.categoryName] || { color: '#64748b', Icon: Settings2 };

  return (
    <div className="im-card" style={{ '--card-color': color }}>
      <div className="im-card__icon">
        {LOGO_URLS[integration.type] ? <LogoImg type={integration.type} size={22} /> : Icon ? <Icon size={18} /> : <Server size={18} />}
      </div>
      <div className="im-card__body">
        <div className="im-card__name">{integration.name || integration.displayName}</div>
        <div className="im-card__type">{integration.displayName || integration.type}</div>
        {integration.connectionMethod === 'AGENT_TUNNEL' && (
          <span className="im-card__tunnel-badge">Agent Tunnel</span>
        )}
      </div>
      <div className="im-card__dot" title={integration.enabled ? 'Enabled' : 'Disabled'} />
      <div className="im-card__actions">
        <button className="im-icon-btn" onClick={() => onTest(integration.id)} title="Test connection">
          <RefreshCw size={14} />
        </button>
        <button className="im-icon-btn" onClick={() => onEdit(integration)} title="Edit">
          <Edit2 size={14} />
        </button>
        <button className="im-icon-btn im-icon-btn--danger" onClick={() => onDelete(integration.id)} title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const IntegrationsManager = () => {
  const [integrations, setIntegrations] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [vmsGroups, setVmsGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [configForm, setConfigForm] = useState({});
  const [connectionMethod, setConnectionMethod] = useState('DIRECT');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  // Lock background scroll while the wizard is open so mobile touch scrolling
  // stays inside the modal body instead of falling through to the page. Capture
  // and restore the prior overflow value rather than hardcoding it, and clean
  // up on close/unmount.
  useEffect(() => {
    if (!showWizard) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [showWizard]);

  const loadData = async () => {
    try {
      const [intRes, typesRes, agentsRes] = await Promise.all([
        fetchWithTenant('/api/integrations'),
        fetchWithTenant('/api/integrations/types'),
        fetchWithTenant('/api/md/agents')
      ]);

      if (intRes.ok)    setIntegrations((await intRes.json()).integrations || []);
      if (typesRes.ok)  setAvailableTypes((await typesRes.json()).types || []);
      if (agentsRes.ok) { const d = await agentsRes.json(); setAgents(d.agents || d || []); }

      try {
        const vmsRes = await fetchWithTenant('/api/vms/intercom-cameras');
        if (vmsRes.ok) setVmsGroups((await vmsRes.json()).vmsGroups || []);
      } catch (_) {}
    } catch (err) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const groupedTypes = availableTypes.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const groupedIntegrations = integrations.reduce((acc, i) => {
    const cat = i.categoryName || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(i);
    return acc;
  }, {});

  // ── Wizard open/close ──

  const openWizard = () => {
    setEditingIntegration(null);
    setStep(0);
    setSelectedType(null);
    setConfigForm({});
    setConnectionMethod('DIRECT');
    setSelectedAgentId('');
    setTestResult(null);
    setShowWizard(true);
  };

  const openEdit = (integration) => {
    const typeInfo = availableTypes.find(t => t.type === integration.type);
    setEditingIntegration(integration);
    setSelectedType(typeInfo || { type: integration.type, name: integration.displayName, category: integration.categoryName, configSchema: {} });
    setConfigForm(integration.config || {});
    setConnectionMethod(integration.connectionMethod || 'DIRECT');
    setSelectedAgentId(integration.agentId || '');
    setTestResult(null);
    setStep(1); // Skip type selection when editing
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setEditingIntegration(null);
    setSelectedType(null);
  };

  // ── Wizard navigation ──

  const handleSelectType = (type) => {
    setSelectedType(type);
    const defaults = {};
    // Extract defaults from properties (JSON Schema) or fields (legacy)
    if (type.configSchema?.properties) {
      Object.entries(type.configSchema.properties).forEach(([k, p]) => {
        if (p.default !== undefined) defaults[k] = p.default;
      });
    } else if (Array.isArray(type.configSchema?.fields)) {
      for (const f of type.configSchema.fields) {
        if (f.default !== undefined) defaults[f.key] = f.default;
      }
    }
    setConfigForm(defaults);
    // Default connection method from schema (e.g. Genetec always needs agent tunnel)
    if (type.configSchema?.connectionMethod) {
      setConnectionMethod(type.configSchema.connectionMethod);
    }
    setStep(1);
  };

  const canAdvance = () => {
    if (step === 1) {
      if (!selectedType) return false;
      const required = selectedType.configSchema?.required
        || (selectedType.configSchema?.fields || []).filter(f => f.required).map(f => f.key)
        || [];
      return required.every(k => configForm[k] !== undefined && configForm[k] !== '');
    }
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step === 1 && !editingIntegration) { setStep(0); setSelectedType(null); }
    else if (step > 0) setStep(s => s - 1);
  };

  // ── Save ──

  const handleSave = async () => {
    setSaving(true);
    try {
      const tunnelEndpoints = (connectionMethod === 'AGENT_TUNNEL' && configForm.host)
        ? [{ name: configForm.name || selectedType.name, url: `${configForm.useHttps !== false ? 'https' : 'http'}://${configForm.host}${configForm.port ? ':' + configForm.port : ''}` }]
        : null;

      const body = {
        name: configForm.name || selectedType.name,
        config: configForm,
        connectionMethod,
        agentId: selectedAgentId || null,
        tunnelEndpoints,
        ...(!editingIntegration && { type: selectedType.type })
      };

      const res = await fetchWithTenant(
        editingIntegration ? `/api/integrations/${editingIntegration.id}` : '/api/integrations',
        { method: editingIntegration ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );

      if (res.ok) {
        toast.success(editingIntegration ? 'Integration updated' : 'Integration added');
        closeWizard();
        loadData();
      } else {
        const d = await res.json();
        toast.error(d.error || 'Failed to save integration');
      }
    } catch (err) {
      toast.error('Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete / Test existing ──

  const handleDelete = async (id) => {
    if (!confirm('Delete this integration? This cannot be undone.')) return;
    const res = await fetchWithTenant(`/api/integrations/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Integration deleted'); loadData(); }
    else toast.error('Failed to delete integration');
  };

  const handleTestExisting = async (id) => {
    try {
      const res = await fetchWithTenant(`/api/integrations/${id}/test`, { method: 'POST' });
      const d = await res.json();
      d.success ? toast.success(d.message || 'Connection successful') : toast.error(d.error || 'Connection failed');
    } catch { toast.error('Test failed'); }
  };

  // ── Footer button logic ──

  const renderFooter = () => {
    if (step === 0) return null; // Step 0 uses card clicks

    const isLastStep = step === STEPS.length - 1;

    return (
      <div className="im-wizard__footer">
        <button className="im-btn im-btn--ghost" onClick={handleBack}>
          <ChevronLeft size={16} /> Back
        </button>
        <div className="im-wizard__footer-right">
          {!isLastStep && (
            <button
              className="im-btn im-btn--primary"
              onClick={handleNext}
              disabled={!canAdvance()}
            >
              {step === 2 ? 'Review' : 'Continue'} <ChevronRight size={16} />
            </button>
          )}
          {isLastStep && (
            <button
              className="im-btn im-btn--save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><Loader2 size={14} className="im-spin" /> Saving…</> : <><Check size={14} /> {editingIntegration ? 'Update Integration' : 'Save Integration'}</>}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="im-loading">
        <Loader2 size={24} className="im-spin" />
        <span>Loading integrations…</span>
      </div>
    );
  }

  return (
    <div className="im-root">
      {/* Header */}
      <div className="im-header">
        <div>
          <h2 className="im-header__title">Integrations</h2>
          <p className="im-header__sub">{integrations.length} integration{integrations.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button className="im-btn im-btn--primary" onClick={openWizard}>
          <Plus size={16} /> Add Integration
        </button>
      </div>

      {/* Existing integrations by category */}
      {Object.entries(CATEGORIES).map(([category, { label, Icon, color }]) => {
        const items = groupedIntegrations[category] || [];
        return (
          <div key={category} className="im-category-section">
            <div className="im-category-header" style={{ '--cat-color': color }}>
              <Icon size={15} />
              <span>{label}</span>
              {items.length > 0 && <span className="im-category-count">{items.length}</span>}
            </div>
            {items.length === 0 ? (
              <div className="im-empty">
                No {label.toLowerCase()} integrations configured
              </div>
            ) : (
              <div className="im-cards">
                {items.map(int => (
                  <IntegrationCard
                    key={int.id}
                    integration={int}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                    onTest={handleTestExisting}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Wizard Modal ── */}
      {showWizard && (
        <div className="im-overlay" onClick={closeWizard}>
          <div className="im-wizard" onClick={e => e.stopPropagation()}>

            {/* Wizard Header */}
            <div className="im-wizard__header">
              <div className="im-wizard__title">
                {editingIntegration
                  ? `Edit ${selectedType?.name || 'Integration'}`
                  : step === 0 ? 'Add Integration' : `Add ${selectedType?.name || 'Integration'}`
                }
              </div>
              <button className="im-close" onClick={closeWizard}><X size={20} /></button>
            </div>

            {/* Step Indicator */}
            <StepIndicator current={step} />

            {/* Step Body */}
            <div className="im-wizard__body">
              {step === 0 && (
                <StepChooseSystem
                  groupedTypes={groupedTypes}
                  onSelect={handleSelectType}
                />
              )}
              {step === 1 && selectedType && (
                <StepConfigure
                  selectedType={selectedType}
                  configForm={configForm}
                  setConfigForm={setConfigForm}
                  connectionMethod={connectionMethod}
                  setConnectionMethod={setConnectionMethod}
                  selectedAgentId={selectedAgentId}
                  setSelectedAgentId={setSelectedAgentId}
                  agents={agents}
                  vmsGroups={vmsGroups}
                />
              )}
              {step === 2 && selectedType && (
                <StepTestConnection
                  selectedType={selectedType}
                  configForm={configForm}
                  connectionMethod={connectionMethod}
                  selectedAgentId={selectedAgentId}
                  testResult={testResult}
                  setTestResult={setTestResult}
                />
              )}
              {step === 3 && selectedType && (
                <StepReview
                  selectedType={selectedType}
                  configForm={configForm}
                  connectionMethod={connectionMethod}
                  selectedAgentId={selectedAgentId}
                  testResult={testResult}
                  agents={agents}
                />
              )}
            </div>

            {/* Footer */}
            {renderFooter()}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsManager;
